/**
 * The bridge that makes the palette switchable at runtime WITHOUT rewriting the
 * thousands of `theme.colors.*` call sites.
 *
 * On web, `theme.colors.x` resolves to `cssVarColors.x`, a string like
 *   var(--tc-background-primary, #000000)
 * The fallback is the DARK literal, so anywhere the variable stylesheet has not
 * loaded — and on React Native, which never imports this — the app renders
 * exactly today's dark palette. Injecting the stylesheet (injectThemeVars) and
 * flipping `data-theme` on <html> is then the entire switch: the browser
 * repaints, no component re-renders.
 *
 * The variable NAMES and the shape of `cssVarColors` are both derived from the
 * one palette source, so a token can never exist in one and not the other.
 */
import { colors as darkColors, type Colors } from './colors';
import { sectionColorSpec } from './sectionColor';
import { lightColors, type LightColors } from './colors.light';
import { colorTokens, flattenTokens, tokenCssVar } from './tokens';
import { compactTouchCSS } from './textRoleStyles';

// Compile-time guards, BOTH directions: a variable assignment only catches
// MISSING keys (excess-property checking applies to object literals, not
// variables), so one direction alone would let a token added to only one
// palette compile clean and silently never emit its `--tc-*` var.
const _shapeGuard: Colors = lightColors; // light must cover every dark key
const _reverseGuard: LightColors = darkColors; // and dark must cover every light key
void _shapeGuard;
void _reverseGuard;

type AnyColors = Record<string, unknown>;

/** Flatten a nested palette to { '--tc-a-b-c': value }. */
function flatten(obj: AnyColors, prefix = '--tc'): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    const name = `${prefix}-${key}`;
    if (value && typeof value === 'object') {
      Object.assign(out, flatten(value as AnyColors, name));
    } else {
      out[name] = String(value);
    }
  }
  return out;
}

/** Same shape as the input, but every leaf becomes `var(--tc-…, <fallback>)`. */
function toVars<T extends AnyColors>(obj: T, prefix = '--tc'): T {
  const out: AnyColors = {};
  for (const [key, value] of Object.entries(obj)) {
    const name = `${prefix}-${key}`;
    out[key] = value && typeof value === 'object' ? toVars(value as AnyColors, name) : `var(${name}, ${value})`;
  }
  return out as T;
}

/** The web-facing palette: identical shape to `colors`, values are CSS vars. */
export const cssVarColors: Colors = toVars(darkColors as unknown as AnyColors) as unknown as Colors;

/** The semantic tokens (./tokens) as { '--tc-color-…': value }. */
const tokenFlat = (scheme: 'light' | 'dark'): Record<string, string> =>
  Object.fromEntries(Object.entries(flattenTokens(colorTokens[scheme])).map(([path, value]) => [tokenCssVar(path), value]));

// Legacy palette vars (--tc-background-*, --tc-text-*, …) plus the semantic
// token vars (--tc-color-*), written into the same theme blocks so both switch
// together. The prefixes cannot collide: no legacy palette group is named "color".
const darkFlat = { ...flatten(darkColors as unknown as AnyColors), ...tokenFlat('dark') };
const lightFlat = { ...flatten(lightColors as unknown as AnyColors), ...tokenFlat('light') };

const block = (flat: Record<string, string>): string =>
  Object.entries(flat)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');

/**
 * Class that pins a subtree to the DARK token set regardless of the app theme.
 *
 * The public marketing pages paint their own grounds from `publicTheme` —
 * literal blacks (#000 / #0C0C0D / #161618), by brand design, not tokens. Their
 * TEXT, however, goes through `var(--tc-…)`. In light mode the text followed
 * the theme while the ground stayed black, so the marketing site rendered
 * near-black text on black: `--tc-overlay-scrim` resolved to rgba(0,0,0,0.16)
 * against #161618, a contrast ratio of 1.03:1. Invisible.
 *
 * Pinning the subtree keeps those two halves in agreement. It is deliberately a
 * class and not `data-theme`, because the token blocks below are `:root`-
 * anchored and cannot be re-scoped to a descendant.
 */
export const PUBLIC_SURFACE_CLASS = 'tc-public-surface';

/**
 * The variable stylesheet. `:root` is LIGHT; explicit dark and OS-dark redefine
 * the tokens to DARK; an explicit light choice beats OS-dark. `color-scheme`
 * makes native controls, scrollbars, and form widgets follow along for free.
 *
 * The trailing `.tc-public-surface` block re-declares the dark tokens on a
 * descendant element. Specificity never competes here — CSS only compares
 * declarations set on the SAME element — so the subtree's own values win over
 * whatever it inherits from :root, whichever theme is active.
 */
export function generateThemeCSS(): string {
  // A section colour's share of its band (sectionColorSpec), themed with the tokens.
  const tint = (share: number) => `\n  --tc-section-tint: ${Math.round(share * 100)}%;`;
  const light = block(lightFlat) + tint(sectionColorSpec.bandTint.light);
  const dark = block(darkFlat) + tint(sectionColorSpec.bandTint.dark);
  return [
    `:root{\n  color-scheme: light;\n${light}\n}`,
    `@media (prefers-color-scheme: dark){\n  :root:not([data-theme="light"]){\n  color-scheme: dark;\n${dark}\n  }\n}`,
    `:root[data-theme="dark"]{\n  color-scheme: dark;\n${dark}\n}`,
    `:root[data-theme="light"]{\n  color-scheme: light;\n${light}\n}`,
    `.${PUBLIC_SURFACE_CLASS}{\n  color-scheme: dark;\n${dark}\n}`,
    compactTouchCSS(),
  ].join('\n\n');
}

let injected = false;

/** Inject the variable stylesheet once, at web boot. No-op on React Native. */
export function injectThemeVars(): void {
  if (injected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'tc-theme-vars';
  style.setAttribute('data-tc-theme', 'vars');
  style.textContent = generateThemeCSS();
  document.head.appendChild(style);
  injected = true;
}
